import { gql } from "@apollo/client";
import type { SyncableDisease } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { Disease as RxDisease } from "../collections/disease";
import type { RxDBCollections } from "../provider";
import {
  SyncDiseasePullDocument,
  type WholeSyncableDiseaseFragment,
} from "./__generated__/disease.gql";
import { useGqlReplication } from "./use-gql-replication";

export const useReplicateDisease = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxDisease>;
}) => {
  const pull = useMemo(
    () => ({
      query: SyncDiseasePullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { hours: 1 },
    }),
    [],
  );

  return useGqlReplication<RxDisease, SyncableDisease, null>({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlDiseaseToRxDisease,
    batchSize: 1000,
    metadataLocalStorageKeySuffix: "disease",
  });
};

export const mapGqlDiseaseToRxDisease = (
  disease: WholeSyncableDiseaseFragment,
): WithDeleted<RxDisease> => ({
  id: disease.id,
  category: disease.category,
  catalogueCoding: disease.catalogueCoding
    ? {
        icd10: disease.catalogueCoding.icd10
          ? {
              code: disease.catalogueCoding.icd10.code,
              system: disease.catalogueCoding.icd10.system ?? null,
            }
          : null,
        icd11: disease.catalogueCoding.icd11
          ? {
              code: disease.catalogueCoding.icd11.code,
              system: disease.catalogueCoding.icd11.system ?? null,
            }
          : null,
      }
    : null,
  i18n: disease.i18n.map((i18n) => ({
    locale: i18n.locale,
    categoryDisplayName: i18n.categoryDisplayName,
    displayName: i18n.displayName,
    keywords: i18n.keywords.map((keyword) => keyword),
  })),
  meta: {
    createdAt: disease.meta.createdAt,
    createdByUserId: disease.meta.createdByUserId,
    updatedAt: disease.meta.updatedAt,
    updatedByUserId: disease.meta.updatedByUserId,
  },
  _deleted: disease.meta.deleted,
});

gql`
  fragment WholeSyncableDisease on SyncableDisease {
      id
      category
      catalogueCoding {
        icd10 {
            code
            system
        }
        icd11 {
            code
            system
        }
      }
      i18n {
        locale
        categoryDisplayName
        displayName
        keywords
      }
      meta {
        createdAt
        updatedAt
        createdByUserId
        updatedByUserId
        deleted
      }
  }
`;

gql`
  query SyncDiseasePull($input: PullDiseaseInput!) {
    pullDisease(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableDisease
      }
    }
  }
`;
